import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { parseISO } from 'date-fns';
import type { DeviceDetails, TVDashboardData } from '@sharedtypes/models';
import {
  ApiRoutes, API_ROUTES, DeviceEventsResponse,
} from '@sharedtypes/apiRoutes';
import ClientAppError from './utils/clientAppError';
import { handleApiError } from '@modules/utils/errorHandler';
import { setTimeToMidnight } from './utils/dateUtils';
import { getFirebaseIdToken } from './authService';

/**
 * Get the id token from the current user and make an authenticated API call
 * to an AxiosResponse object.
 */
async function makeAuthenticatedApiCall<T>(
  options: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  const idToken = await getFirebaseIdToken();
  if (!idToken) {
    throw ClientAppError.authError();
  }
  const updatedOptions = {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `${idToken}`,
    },
  };
  return axios.request(updatedOptions);
}

type FuelRegenHour = ApiRoutes[typeof API_ROUTES.FUEL_REGEN]['get']['response'];
async function getFuelRegen(deviceId: number): Promise<FuelRegenHour> {
  try {
    const axiosOptions = {
      method: 'GET',
      url: `/api/fuelregen/${deviceId}`,
    };
    const response = await makeAuthenticatedApiCall<FuelRegenHour>(axiosOptions);
    return response.data;
  } catch (error) {
    return handleApiError(
      error,
      'apiService.getFuelRegen',
      `Error fetching fuel regen data for device ${deviceId}`,
    );
  }
}

const deviceDetails: DeviceDetails[] = [];

async function getDeviceDetails(cacheStore=deviceDetails): Promise<DeviceDetails[]> {
  // Authentication is handled by makeAuthenticatedApiCall
  const options = {
    url: '/api/devices',
    method: 'GET',
  };
  if (cacheStore.length > 0) return cacheStore;

  try {
    const response = await makeAuthenticatedApiCall<DeviceDetails[]>(options);
    // Directly push all device objects, preserving all properties
    cacheStore.push(...response.data);
    return cacheStore;
  } catch (error) {
    return handleApiError(
      error,
      'apiService.getDeviceDetails',
      'Error fetching device details',
    );
  }
}

/**
 * Get all events for a device over a 24-hour period starting from the provided date
 * @param deviceId The device ID to fetch events for
 * @param date A date object representing the date to get events for
 * @returns Promise with events and metadata
 */
async function getDeviceEventsByDate(
  deviceId: number,
  date: Date | string,
): Promise<DeviceEventsResponse> {
  try {
    // Convert input to proper Date object at start of day in local timezone
    let dateObj: Date;

    if (typeof date === 'string') {
      // Parse the string as a date, handling both "YYYY-MM-DD" and ISO formats
      dateObj = parseISO(date);
    } else {
      // Use the provided Date object
      dateObj = date;
    }

    // Validate the date
    if (!dateObj || Number.isNaN(dateObj.getTime())) {
      throw ClientAppError.invalidInput('Invalid date parameter received in getDeviceEventsByDate');
    }

    // Use setTimeToMidnight to ensure we start at beginning of day in local time
    const startOfDay = setTimeToMidnight(dateObj);

    // Convert to ISO string for the API
    const startTimeIso = startOfDay.toISOString();

    // Prepare the request options
    const options = {
      method: 'GET',
      url: `/api/events/device/${deviceId}/from/${startTimeIso}`,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    // Make the API call
    const response = await makeAuthenticatedApiCall<DeviceEventsResponse>(options);

    // Return the response data
    return response.data;
  } catch (error) {
    return handleApiError(
      error,
      'apiService.getDeviceEventsByDate',
      `Error fetching events for device ${deviceId} on date ${date}`,
    );
  }
}

/**
 * Get TV dashboard data for a specific UUID (no authentication required)
 * @param uuid The public UUID for the dashboard
 * @returns Promise with TV dashboard data or null if invalid/inactive
 */
async function getTVDashboardData(uuid: string): Promise<TVDashboardData | null> {
  try {
    const options = {
      method: 'GET',
      url: `/api/tv/${uuid}`,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    // This is a public endpoint, so we don't use makeAuthenticatedApiCall
    const response = await axios.request<TVDashboardData>(options);
    return response.data;
  } catch (error) {
    // Handle 404 (invalid/inactive UUID) by returning null
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    
    return handleApiError(
      error,
      'apiService.getTVDashboardData',
      `Error fetching TV dashboard data for UUID ${uuid}`,
    );
  }
}

// Export the function
export { getDeviceEventsByDate, getTVDashboardData };

export {
  getFuelRegen,
  makeAuthenticatedApiCall, getDeviceDetails,
};
